import { useState } from 'react';
import { Table, Progress, ResponseErrorPanel } from '@backstage/core-components';
import { useRouteRef } from '@backstage/frontend-plugin-api';
import { createCalendarTableColumns } from './tableColumns';
import { CalendarContainer } from './tableContainer';
import { eventDetailsRouteRef } from '../../routes';
import { useCalendarEvents } from '../../hooks/useCalendarEvents';
import { EventData } from '../../types/EventData';
import { TruncationLimits } from '../../types/TruncationLimits';
import { hasEventTags, UNTAGGED } from '../../utils/EventHelpers';
import { Tags } from '../common/Tags';

type EventListProps = {
  showToolbar?: boolean;
  rowLimit?: number;
  showFull?: boolean;
  showTags?: boolean;
  widgetMode?: boolean;
};
const defaultPageSize = 10;

export const EventList = ({
  showToolbar,
  rowLimit,
  showFull,
  showTags,
  widgetMode = false,
}: EventListProps) => {
  const getDetailsPath = useRouteRef(eventDetailsRouteRef);
  if (!getDetailsPath) {
    throw new Error(
      'The route for eventDetails is not defined. Ensure the community-calendar plugin is installed correctly.',
    );
  }

  const storageKey = 'varia.calendar.filter';
  const [filter, setFilter] = useState<string[]>(() => {
    const stored = localStorage.getItem(storageKey);
    return JSON.parse(stored ?? '[]');
  });

  const truncationLimits: TruncationLimits = {
    title: showFull ? 240 : 90,
    location: 26,
    description: 250,
  };

  const { eventRows, tags, isLoading, error } = useCalendarEvents(truncationLimits);

  if (isLoading) {
    return <Progress />;
  }

  if (error) {
    return <ResponseErrorPanel error={error} />;
  }

  const columns = createCalendarTableColumns({
    showTags,
    getDetailsPath,
  });

  const tagFilterPredicate = (event: EventData) =>
    filter.length === 0 ||
    (!hasEventTags(event) && filter.includes(UNTAGGED)) ||
    event.tags.some(tag => filter.includes(tag));

  if (!eventRows) {
    return <p>No events found</p>;
  }
  const filtered = eventRows.filter(tagFilterPredicate);
  const unlimited = filtered.length ? filtered : eventRows;
  const eventTableRows = unlimited.slice(0, rowLimit);

  const updateFilter = (filters: string[]) => {
    const removedUnusedFilters = filters.filter(f => tags.includes(f));
    setFilter(removedUnusedFilters);
    localStorage.setItem(storageKey, JSON.stringify(removedUnusedFilters));
  };

  return (
    eventTableRows.length && (
      <CalendarContainer widgetMode={widgetMode}>
        {!showToolbar && <Tags tags={tags} filter={filter} onUpdate={updateFilter} />}
        {showToolbar && (
          <div
            style={{
              padding: '4px 16px 8px 16px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
            }}
          />
        )}
        <Table
          options={{
            search: showToolbar,
            paging: showToolbar,
            toolbar: showToolbar,
            padding: 'dense',
            pageSize: defaultPageSize,
            pageSizeOptions: [defaultPageSize, defaultPageSize * 2, defaultPageSize * 5],
            searchFieldAlignment: 'right',
          }}
          localization={{
            toolbar: {
              searchPlaceholder: 'Search',
              searchTooltip: 'Search',
            },
          }}
          style={{ tableLayout: 'fixed' }}
          columns={columns}
          data={eventTableRows}
          title={showToolbar ? <Tags tags={tags} filter={filter} onUpdate={updateFilter} /> : <></>}
        />
      </CalendarContainer>
    )
  );
};
